import React, { useState, useEffect, useRef } from 'react'
import { LOOKS, LOOK_ORDER, FONTS, resolveTheme, normHex, luminance, googleFontsHref, fontStack } from '../email/theme'
import { ColorField } from './ColorField'

// The email's brand: one look, one brand colour, the logo, fonts, buttons and
// corners. Every section reads from this, so a change here re-styles the
// whole email at once (and "Save as my brand" makes it the start of every
// new email).

const SWATCHES = [
  { hex: '#6E1A2E', name: 'Burgundy' },
  { hex: '#C4552D', name: 'Terracotta' },
  { hex: '#E4572E', name: 'Coral' },
  { hex: '#D63A6E', name: 'Pink' },
  { hex: '#5B2A4E', name: 'Plum' },
  { hex: '#A67C2E', name: 'Gold' },
  { hex: '#1F4B39', name: 'Forest' },
  { hex: '#5E7D63', name: 'Sage' },
  { hex: '#007EA0', name: 'Teal' },
  { hex: '#3F6F76', name: 'Petrol' },
  { hex: '#1F2F4A', name: 'Navy' },
  { hex: '#29282D', name: 'Charcoal' },
]

let allFontsRequested = false
const loadAllFonts = () => {
  if (allFontsRequested || typeof document === 'undefined') return
  allFontsRequested = true
  const href = googleFontsHref(Object.keys(FONTS))
  if (!href) return
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  document.head.appendChild(link)
}

const Check = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><polyline points="20 6 9 17 4 12" /></svg>
)

const LookTile = ({ id, active, accent, onPick }) => {
  const t = resolveTheme({ look: id, accent })
  const p = t.palette
  const upper = t.type.displayCase === 'upper'
  const radius = t.button.shape === 'pill' ? 999 : t.button.shape === 'rounded' ? 5 : 1
  const outline = t.button.style === 'outline'
  return (
    <button type="button" className={`bp-look ${active ? 'is-on' : ''}`} onClick={() => onPick(id)} aria-pressed={active}>
      <span className="bp-look-art" style={{ background: p.canvas }}>
        <span className="bp-look-card" style={{ background: p.paper, borderRadius: Math.min(t.radius, 8) }}>
          <span className="bp-look-kicker" style={{ color: p.accentText }}>New</span>
          <span className="bp-look-head" style={{ fontFamily: fontStack(t.type.display), fontWeight: t.type.displayWeight, textTransform: upper ? 'uppercase' : 'none', letterSpacing: upper ? '0.02em' : '-0.01em', color: p.ink }}>
            {upper ? 'Fresh cut' : t.type.italicAccent ? <>Hello <em>you</em></> : 'Hello you'}
          </span>
          <span className="bp-look-btn" style={{ background: outline ? 'transparent' : p.accent, border: `1.5px solid ${outline ? p.accentText : p.accent}`, borderRadius: radius }} />
        </span>
      </span>
      <span className="bp-look-name">{LOOKS[id].name}{active && <span className="bp-look-tick"><Check /></span>}</span>
      <span className="bp-look-blurb">{LOOKS[id].blurb}</span>
    </button>
  )
}

// The salon's own brand as the first look: its look, colour, type, buttons
// and logo together, exactly as saved.
const BrandTile = ({ brand, active, onPick }) => {
  const t = resolveTheme(brand)
  const p = t.palette
  const upper = t.type.displayCase === 'upper'
  const radius = t.button.shape === 'pill' ? 999 : t.button.shape === 'rounded' ? 5 : 1
  const outline = t.button.style === 'outline'
  const round = t.logo && t.logoShape !== 'natural'
  return (
    <button type="button" className={`bp-look bp-look--brand ${active ? 'is-on' : ''}`} onClick={onPick} aria-pressed={active}>
      <span className="bp-look-art" style={{ background: p.canvas }}>
        <span className="bp-look-card" style={{ background: p.paper, borderRadius: Math.min(t.radius, 8) }}>
          {t.logo
            ? <img className={`bp-look-logo ${round ? 'is-round' : ''}`} src={t.logo} alt="" />
            : <span className="bp-look-kicker" style={{ color: p.accentText }}>{t.name || 'Your salon'}</span>}
          <span className="bp-look-head" style={{ fontFamily: fontStack(t.type.display), fontWeight: t.type.displayWeight, textTransform: upper ? 'uppercase' : 'none', letterSpacing: upper ? '0.02em' : '-0.01em', color: p.ink }}>
            {upper ? 'Fresh cut' : t.type.italicAccent ? <>Hello <em>you</em></> : 'Hello you'}
          </span>
          <span className="bp-look-btn" style={{ background: outline ? 'transparent' : p.accent, border: `1.5px solid ${outline ? p.accentText : p.accent}`, borderRadius: radius }} />
        </span>
      </span>
      <span className="bp-look-name">Your brand{active && <span className="bp-look-tick"><Check /></span>}</span>
      <span className="bp-look-blurb">Your logo, colour and type, as saved</span>
    </button>
  )
}

// Whether an email is wearing the salon's brand as saved
const BRAND_KEYS = ['look', 'accent', 'display', 'text', 'button', 'radius', 'logo']
export const isOnBrand = (spec, brand) => !!brand && BRAND_KEYS.every(k => JSON.stringify((spec || {})[k] || null) === JSON.stringify(brand[k] || null))

const Seg = ({ value, options, onChange, label }) => (
  <div className="bp-row">
    <span className="bp-row-label">{label}</span>
    <div className="se-seg">
      {options.map(o => (
        <button key={String(o.value)} type="button" className={value === o.value ? 'is-on' : ''} onClick={() => onChange(o.value)} aria-pressed={value === o.value}>{o.label}</button>
      ))}
    </div>
  </div>
)

// Font choice, each option shown in its own face.
const FontSelect = ({ label, value, lookDefault, onChange }) => {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)
  const current = value || lookDefault
  // Clicking or tapping anywhere else closes the list; so does Escape, which
  // stops there rather than also closing the whole panel
  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false) } }
    document.addEventListener('pointerdown', onDown, true)
    window.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('pointerdown', onDown, true)
      window.removeEventListener('keydown', onKey, true)
    }
  }, [open])
  const groups = [
    { kind: 'serif', label: 'Serif' },
    { kind: 'condensed', label: 'Condensed' },
    { kind: 'sans', label: 'Sans serif' },
  ]
  return (
    <div className="bp-font" ref={rootRef}>
      <span className="bp-row-label">{label}</span>
      <button type="button" className="bp-font-btn" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span style={{ fontFamily: fontStack(current) }}>{FONTS[current] ? FONTS[current].label : 'System'}</span>
        <span className="bp-font-meta">{value ? '' : 'Look default'}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open && (
        <div className="bp-font-list">
          <button type="button" className={`bp-font-opt ${!value ? 'is-on' : ''}`} onClick={() => { onChange(undefined); setOpen(false) }}>
            <span style={{ fontFamily: fontStack(lookDefault) }}>{FONTS[lookDefault] ? FONTS[lookDefault].label : 'System'}</span>
            <span className="bp-font-meta">Look default</span>
          </button>
          {groups.map(g => (
            <React.Fragment key={g.kind}>
              <p className="bp-font-group">{g.label}</p>
              {Object.keys(FONTS).filter(k => FONTS[k].kind === g.kind).map(k => (
                <button key={k} type="button" className={`bp-font-opt ${value === k ? 'is-on' : ''}`} onClick={() => { onChange(k); setOpen(false) }}>
                  <span style={{ fontFamily: fontStack(k), fontSize: FONTS[k].kind === 'condensed' ? 15 : 16, textTransform: FONTS[k].kind === 'condensed' ? 'uppercase' : 'none' }}>{FONTS[k].label}</span>
                </button>
              ))}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  )
}

// `only` shows one section (the phone editor opens them one at a time): look, colour, logo, fonts, buttons or corners
export const BrandPanel = ({ spec, salonKit, onChange, onPickLogo, kitSaved, brand, onUseBrand, onPickLook, only }) => {
  useEffect(() => { loadAllFonts() }, [])
  const s = spec || { look: 'letter' }
  const avatar = s.logo && (s.logoShape === 'round' || s.logoShape === 'square')
  const theme = resolveTheme(s)
  const look = LOOKS[theme.look]
  const onBrand = isOnBrand(s, brand)
  const set = (patch) => {
    const next = { ...s, ...patch }
    Object.keys(next).forEach(k => { if (next[k] === undefined || next[k] === '') delete next[k] })
    onChange(next)
  }
  const accent = normHex(s.accent)
  const lookAccent = normHex(look.palette.accent)
  const salonAccent = salonKit && normHex(salonKit.accent)
  const pickAccent = (hex) => set({ accent: normHex(hex) === lookAccent ? undefined : normHex(hex) })
  const shownAccent = accent || lookAccent
  const swatches = [
    { hex: lookAccent, name: `${look.name} colour` },
    ...(salonAccent && salonAccent !== lookAccent ? [{ hex: salonAccent, name: 'Your brand colour' }] : []),
    ...SWATCHES.filter(sw => sw.hex !== lookAccent && sw.hex !== salonAccent),
  ]
  const custom = accent && !swatches.some(sw => sw.hex === accent)

  return (
    <div className="bp">
      <section className="bp-sec" hidden={!!only && only !== 'look'}>
        <h4 className="bp-h">Look</h4>
        <p className="bp-sub">{brand ? 'Your brand first. The other looks change this email only, and your brand stays as it is.' : 'Type, colours and buttons that work together. Your content stays put.'}</p>
        <div className="bp-looks">
          {brand && <BrandTile brand={brand} active={onBrand} onPick={onUseBrand} />}
          {LOOK_ORDER.map(id => (
            <LookTile
              key={id}
              id={id}
              active={!onBrand && theme.look === id}
              accent={brand ? undefined : s.accent}
              onPick={(lookId) => (brand && onPickLook ? onPickLook(lookId) : set({ look: lookId }))}
            />
          ))}
        </div>
        {brand && !onBrand && (
          <p className="bp-offbrand">
            This email uses {look.name}. Changes to colour, type, buttons, corners or logo still save to your brand.
            {' '}<button type="button" className="bp-link bp-link--inline" onClick={onUseBrand}>Use your brand</button>
          </p>
        )}
      </section>

      <section className="bp-sec" hidden={!!only && only !== 'colour'}>
        <h4 className="bp-h">Brand colour</h4>
        <p className="bp-sub">Buttons, highlights and brand-coloured sections use it.</p>
        <div className="bp-swatches">
          {swatches.map((sw, i) => {
            const on = shownAccent === sw.hex
            return (
              <button
                key={sw.hex + i}
                type="button"
                className={`bp-swatch ${on ? 'is-on' : ''}`}
                style={{ background: sw.hex, color: luminance(sw.hex) > 0.5 ? '#1A1A1E' : '#fff' }}
                onClick={() => pickAccent(sw.hex)}
                title={sw.name}
                aria-label={sw.name}
                aria-pressed={on}
              >
                {on && <Check />}
                {i === 0 && <span className="bp-swatch-tag">Look</span>}
                {salonAccent && sw.hex === salonAccent && i !== 0 && <span className="bp-swatch-tag">Yours</span>}
              </button>
            )
          })}
        </div>
        <div className="bp-custom">
          <ColorField label={custom ? 'Your colour' : 'Or pick any colour'} value={shownAccent} onChange={(hex) => pickAccent(hex)} />
        </div>
        {luminance(shownAccent) > 0.6 && (
          <p className="se-hint">Light colours work best as backgrounds. Text on them switches to dark automatically so it stays readable.</p>
        )}
      </section>

      <section className="bp-sec" hidden={!!only && only !== 'logo'}>
        <h4 className="bp-h">Logo</h4>
        {s.logo ? (
          <>
            <div className={`bp-logo ${avatar ? `is-${s.logoShape}` : ''}`} style={{ background: theme.palette.paper }}>
              <img src={s.logo} alt="Your logo" style={avatar ? { width: 64, height: 64 } : { width: Math.min(theme.logoWidth, 220) }} />
            </div>
            <div className="bp-logo-actions">
              <button type="button" className="bp-btn" onClick={onPickLogo}>Change</button>
              <button type="button" className="bp-btn bp-btn--ghost" onClick={() => set({ logo: undefined, logoShape: undefined, logoShapeFor: undefined })}>Remove</button>
            </div>
            <Seg
              label="Show it as"
              value={avatar ? s.logoShape : 'natural'}
              onChange={(v) => set({ logoShape: v === 'natural' ? undefined : v, logoShapeFor: s.logo })}
              options={[{ value: 'natural', label: 'As it is' }, { value: 'round', label: 'Circle' }, { value: 'square', label: 'Rounded' }]}
            />
            {!avatar && <p className="se-hint">Circle and Rounded crop a photo to fit, with your salon name underneath.</p>}
            {!avatar && (
              <div className="bp-row">
                <span className="bp-row-label">Size</span>
                <div className="se-range">
                  <input type="range" min="72" max="240" step="4" value={theme.logoWidth} onChange={(e) => { const v = Number(e.target.value); set({ logoWidth: v }) }} />
                  <span>{theme.logoWidth}px</span>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <button type="button" className="se-img-add" onClick={onPickLogo}>Add your logo</button>
            {salonKit && salonKit.logo && (
              <button type="button" className="bp-link" onClick={() => set({ logo: salonKit.logo, logoShape: undefined, logoShapeFor: undefined })}>Use the image from Settings</button>
            )}
          </>
        )}
        <div className="se-field" style={{ marginTop: 14 }}>
          <label className="se-label" htmlFor="bp-name">Salon name</label>
          <input id="bp-name" type="text" className="se-input" value={s.name || ''} placeholder={(salonKit && salonKit.name) || 'Your salon name'} onChange={(e) => { const v = e.target.value; set({ name: v || undefined }) }} />
          <p className="se-hint">Shown in the header when there’s no logo, and in the sign-offs.</p>
        </div>
      </section>

      <section className="bp-sec" hidden={!!only && only !== 'fonts'}>
        <h4 className="bp-h">Fonts</h4>
        <FontSelect label="Headlines" value={s.display} lookDefault={look.type.display} onChange={(k) => set({ display: k })} />
        <FontSelect label="Body text" value={s.text} lookDefault={look.type.text} onChange={(k) => set({ text: k })} />
        <p className="se-hint">Gmail and Outlook show a matching system font; Apple Mail and most phones show these.</p>
      </section>

      <section className="bp-sec" hidden={!!only && only !== 'buttons'}>
        <h4 className="bp-h">Buttons</h4>
        <Seg label="Shape" value={theme.button.shape} onChange={(v) => set({ button: { ...(s.button || {}), shape: v } })} options={[{ value: 'pill', label: 'Pill' }, { value: 'rounded', label: 'Rounded' }, { value: 'square', label: 'Square' }]} />
        <Seg label="Fill" value={theme.button.style} onChange={(v) => set({ button: { ...(s.button || {}), style: v } })} options={[{ value: 'solid', label: 'Solid' }, { value: 'outline', label: 'Outline' }]} />
        <Seg label="Lettering" value={theme.button.case} onChange={(v) => set({ button: { ...(s.button || {}), case: v } })} options={[{ value: 'none', label: 'Book now' }, { value: 'upper', label: 'BOOK NOW' }]} />
      </section>

      <section className="bp-sec" hidden={!!only && only !== 'corners'}>
        <h4 className="bp-h">Corners</h4>
        <Seg label="Images and cards" value={theme.radius} onChange={(v) => set({ radius: v })} options={[{ value: 0, label: 'Sharp' }, { value: 8, label: 'Soft' }, { value: 16, label: 'Round' }, { value: 24, label: 'Extra' }]} />
      </section>

      <section className="bp-sec bp-sec--save" hidden={!!only}>
        <p className={`bp-autosave ${kitSaved ? 'is-saved' : ''}`} role="status" aria-live="polite">
          <span className="bp-autosave-mark"><Check /></span>
          {kitSaved ? 'Saved as your brand' : 'Your brand saves as you go'}
        </p>
        <p className="se-hint">New emails and templates start with this logo, colour and type.</p>
        <button type="button" className="bp-link" onClick={() => set({ accent: undefined, display: undefined, text: undefined, button: undefined, radius: undefined })}>
          Reset colour, fonts and buttons to {look.name}
        </button>
      </section>
    </div>
  )
}

export default BrandPanel
